/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { Button as EdsButton, ButtonProps } from "@eds/react/Button";

import { IconPosition } from "@components/elements";
import { AnalyticEventProps } from "@components/types";
import { safeToStringUrl } from "@cosmos/common-utils";
import { css } from "@panda/css";
import { HStack } from "@panda/jsx";

import { EdsIcon, EdsIconType, IconColor, IconSize } from "../eds-icon";
import { CosmosLink } from "../link";

import { button } from "./panda-button";

import type { UrlObject } from "url";

export type EdsVariant = ButtonProps["variant"] | null;
type Url = string | UrlObject;

export type CosmosButtonVariant =
	"outline" | "primary" | "secondary" | "ghost" | "ghostUnderline" | "critical" | "contrast" | null;

export interface CosmosButtonProps {
	// EDS variants changed primary -> cta and secondary -> solid
	//  it is more straight forward to keep ours to prevent data migration
	variant?: CosmosButtonVariant;
	size?: ButtonProps["size"] | null;
	label?: string | null;
	name?: string | null;
	icon?: EdsIconType | null;
	iconPosition?: IconPosition;
	iconSize?: IconSize;
	iconColor?: IconColor;
	loading?: boolean;
	loadingLabel?: string;
	"aria-label"?: string;
	children?: React.ReactNode;
	useEds?: boolean;
	href?: Url | null;
	onClick?: React.MouseEventHandler<HTMLElement>;
	className?: string;
	analytics?: AnalyticEventProps;
	disabled?: boolean;
	title?: string;
	target?: string;
	prefetch?: boolean;
}

export const CosmosButton = ({
	label,
	name,
	children,
	variant = "primary",
	size = "md",
	useEds,
	href,
	icon,
	iconPosition = "start",
	iconSize,
	iconColor,
	className,
	analytics,
	...rest
}: CosmosButtonProps): React.JSX.Element | null => {
	if (!label && !children) {
		return null;
	}

	const finalAnalytics: AnalyticEventProps = {
		eventCategory: analytics?.eventCategory || "button",
		interactionType: analytics?.interactionType || "click",
		eventAction: analytics?.eventAction || "navigation",
		eventLabel: label || "",
		eventValue: {
			...analytics?.eventValue,
			href: safeToStringUrl(href),
			variant,
			component: "CosmosButton",
			standardLabel: name ? name.toLowerCase() : "",
		},
	};

	let iconEl = undefined;
	if (icon) {
		iconEl = <EdsIcon icon={icon} size={iconSize} color={iconColor} />;
	}

	if (useEds) {
		let edsVariant: ButtonProps["variant"];
		if (variant === "primary") {
			edsVariant = "cta";
		} else if (variant === "secondary") {
			edsVariant = "solid";
		} else if (variant === "ghostUnderline") {
			edsVariant = "ghost";
		} else {
			edsVariant = variant || undefined;
		}
		return (
			<EdsButton
				{...rest}
				className={css(
					variant === "ghostUnderline"
						? {
								"& span": {
									textDecoration: "underline",
									textUnderlinePosition: "from-font",
								},
							}
						: {},
				)}
				icon={iconEl}
				href={href || ""}
				variant={edsVariant || "cta"}
				size={size || "md"}
				as={CosmosLink}
				analytics={finalAnalytics}
			>
				{label ?? children}
			</EdsButton>
		);
	}

	let content = label ?? children;
	if (icon) {
		content = (
			<HStack
				gap="eds.dimension.8"
				alignItems="center"
				flexDirection={iconPosition === "start" ? "row-reverse" : "row"}
			>
				{content}
				{iconEl}
			</HStack>
		);
	}

	return (
		<CosmosLink
			className={`cosmos-button ${className || ""} ${variant} ${button({ visual: variant || "primary", size: size || "md" })}`}
			href={href || ""}
			analytics={finalAnalytics}
			{...rest}
		>
			{content}
		</CosmosLink>
	);
};
